import { useCallback, useEffect, useMemo, useState } from "react";
import { postEvent, useDrawerContext, useSnackbarContext } from "@WahooFitness/wahoo-offline-mfe";
import { t } from "@lingui/macro";
import useRecoveryHealthSources, { RecoveryHealthSource } from "./useRecoveryHealthSources";
import RecoveryDeviceChangeDrawer from "./RecoveryDeviceChangeDrawer";

// Tells native which Apple Health device the user chose. Native registers it and saves it as
// `recovery_vital_device_id`, then re-sends the backfill window from it. Handled by
// WFSettingsEventId.recoveryDeviceChanged on iOS; fire-and-forget.
export const RECOVERY_DEVICE_CHANGED_EVENT = "recoveryDeviceChanged";

// The Apple Health devices nights can be built from, one selected at a time. A lone device is shown
// locked on: it is, or is about to become, the default pick. Choosing another device confirms first,
// since the switch rebuilds 90 days; filling an empty pick just writes.
//
// When the account's device is on another phone (native says so; always on Android), it is shown on
// by name and every device here is off. Choosing one confirms first, since it replaces the device that
// phone uses; Android lists no devices, so there is nothing to choose there (TAJ-1641).
const useRecoveryDevicePicker = (enabled: boolean) => {
  const recoveryHealthSources = useRecoveryHealthSources(enabled);
  const { enqueueSnackbar } = useSnackbarContext();
  const { setDrawer, handleClose: closeDrawer } = useDrawerContext();
  // The device chosen on this screen, ahead of native's next reply.
  const [chosenId, setChosenId] = useState<string>();
  // Native re-sends the list whenever its device changes (a choice saved, a default pick landing), and that
  // reply is the truth from then on.
  useEffect(() => {
    setChosenId(undefined);
  }, [recoveryHealthSources]);

  // Only an app that handles RECOVERY_DEVICE_CHANGED_EVENT sends `selectedId` (null when there is no
  // pick); an older one leaves it out, and gets no picker rather than one whose choice goes nowhere.
  const canChoose = recoveryHealthSources?.selectedId !== undefined;
  const sources = useMemo(
    () => (canChoose ? recoveryHealthSources?.sources ?? [] : []),
    [canChoose, recoveryHealthSources]
  );
  const selectedId = chosenId ?? recoveryHealthSources?.selectedId ?? undefined;
  // Native listed only devices with HRV, and the list is complete: an empty one means no device tracks HRV.
  const hrvOnly = canChoose && recoveryHealthSources?.hrvOnly === true;
  // The account's device as native names it, when another phone holds it, until a device here is chosen
  // in its place. The name falls back when the cloud's devices couldn't be read.
  const accountDevice = canChoose ? recoveryHealthSources?.accountDevice : undefined;
  const elsewhereDevice = useMemo(
    () =>
      accountDevice?.onThisPhone === false && chosenId === undefined
        ? { name: accountDevice.name ?? t`Your HRV device` }
        : undefined,
    [accountDevice, chosenId]
  );
  const onAnotherPhone = elsewhereDevice !== undefined;
  // No device here tracking HRV says nothing about the account's device on another phone.
  const noHrvDevices =
    !onAnotherPhone &&
    hrvOnly &&
    sources.length === 0 &&
    recoveryHealthSources?.incomplete === false;
  // A lone device is locked only when it is the pick, or nothing is picked yet.
  const locked =
    !onAnotherPhone && sources.length === 1 && (selectedId == null || selectedId === sources[0].id);

  const choose = useCallback(
    (source: RecoveryHealthSource) => {
      // Every value has to be a string: the host decodes eventData as [String: String].
      postEvent(RECOVERY_DEVICE_CHANGED_EVENT, { sourceId: source.id, name: source.name });
      setChosenId(source.id);
      enqueueSnackbar({
        message: t`${source.name} is set up as your HRV data source.`,
        severity: "success",
      });
    },
    [enqueueSnackbar]
  );

  const handleSelect = useCallback(
    (source: RecoveryHealthSource) => {
      // The selected device can't be switched off: Apple Health always builds from one.
      if (!onAnotherPhone && (locked || source.id === selectedId)) {
        return;
      }
      // Another phone's device is always replaced through the confirmation, whatever this phone's pick.
      if (!onAnotherPhone && !selectedId) {
        choose(source);
        return;
      }
      setDrawer({
        open: true,
        hidePuller: true,
        hideClose: true,
        hideDivider: true,
        hideHeader: true,
        children: (
          <RecoveryDeviceChangeDrawer
            currentDeviceName={
              onAnotherPhone
                ? accountDevice?.name ?? undefined
                : sources.find(({ id }) => id === selectedId)?.name
            }
            newDeviceName={source.name}
            currentOnAnotherPhone={onAnotherPhone}
            onConfirm={() => {
              closeDrawer();
              choose(source);
            }}
            onCancel={closeDrawer}
          />
        ),
      });
    },
    [accountDevice, choose, closeDrawer, locked, onAnotherPhone, selectedId, setDrawer, sources]
  );

  const isSelected = useCallback(
    (source: RecoveryHealthSource) => !onAnotherPhone && (locked || source.id === selectedId),
    [locked, onAnotherPhone, selectedId]
  );

  return {
    sources,
    locked,
    hrvOnly,
    noHrvDevices,
    elsewhereDevice,
    isSelected,
    handleSelect,
  };
};

export default useRecoveryDevicePicker;
